/**
 * Zha Search — Semrush Keyword Filtering & Intent Classifier Engine
 *
 * Implements Part 1 of localhub_automation_architecture.md:
 * 1. Intent Classification (REJECT, TIER_1, TIER_2, TIER_3)
 * 2. Entity Check (rejects proper nouns lacking category or city tokens)
 * 3. Opportunity Scoring Algorithm
 * 4. Bulk Semrush CSV processor
 */

export interface IntentRule {
  pattern: RegExp;
  verdict: 'REJECT' | 'TIER_1' | 'TIER_2' | 'TIER_3';
  reason: string;
}

export const INTENT_RULES: IntentRule[] = [
  // REJECT — Fraud vectors, gambling, raw landmarks, proper names, direct informational
  {
    pattern: /customer care|helpline|toll free|complaint number|whatsapp number/i,
    verdict: 'REJECT',
    reason: 'Fraud vector — fake numbers on directories are an active scam surface in India'
  },
  {
    pattern: /teer|result|lottery|satta|matka|betting/i,
    verdict: 'REJECT',
    reason: 'Gambling — AdSense restricted, off-model'
  },
  {
    pattern: /\b(chowk|circle|bypass|depot|junction|flyover|nagar road|signal)\b/i,
    verdict: 'REJECT',
    reason: 'Geography/landmark query — not a commercial business need'
  },
  {
    pattern: /^(dr|mr|mrs|shri|smt)\.?\s/i,
    verdict: 'REJECT',
    reason: 'Person name — navigational, already satisfied'
  },
  {
    pattern: /pincode|std code|map of|distance from|timings? today/i,
    verdict: 'REJECT',
    reason: 'Informational — Google answers directly in SERP'
  },

  // DOWNGRADE — Structurally contested
  {
    pattern: /near me$/i,
    verdict: 'TIER_3',
    reason: 'Local pack owns above-fold; organic CTR is minimal'
  },

  // KEEP & PRIORITIZE
  {
    pattern: /^(best|top)?\s*(.+?)\s+in\s+(.+)$/i,
    verdict: 'TIER_1',
    reason: 'Category x City commercial directory model'
  },
  {
    pattern: /(charges|cost|price|fees|rate|packages)/i,
    verdict: 'TIER_1',
    reason: 'Commercial informational — guide page with high buyer intent'
  },
  {
    pattern: /(how to|what is|when should|which|guide|tips)/i,
    verdict: 'TIER_2',
    reason: 'Informational buyer guide page'
  }
];

export interface ClassificationResult {
  keyword: string;
  intent_tier: 'TIER_1' | 'TIER_2' | 'TIER_3' | 'REJECTED';
  reject_reason?: string;
  matched_category?: string;
  matched_city?: string;
}

/**
 * Step 1: Classify intent of a keyword using pattern rules
 */
export function classifyKeywordIntent(keyword: string): { verdict: 'REJECT' | 'TIER_1' | 'TIER_2' | 'TIER_3'; reason: string } {
  const trimmed = keyword.trim();
  if (!trimmed || trimmed.length < 3) {
    return { verdict: 'REJECT', reason: 'Query too short or blank' };
  }

  for (const rule of INTENT_RULES) {
    if (rule.pattern.test(trimmed)) {
      return { verdict: rule.verdict, reason: rule.reason };
    }
  }

  // Default fallback for unmatched queries
  return { verdict: 'TIER_1', reason: 'Standard commercial intent' };
}

/**
 * Step 2: Entity Check — Ensures keyword contains a known category OR a known city
 */
export function entityCheck(
  keyword: string,
  categories: string[],
  cities: string[]
): { valid: boolean; matched_category?: string; matched_city?: string; reason?: string } {
  const lower = keyword.toLowerCase();
  
  const matched_category = categories.find(cat => lower.includes(cat.toLowerCase()));
  const matched_city = cities.find(city => lower.includes(city.toLowerCase()));

  if (!matched_category && !matched_city) {
    return {
      valid: false,
      reason: 'Proper noun check failed — keyword contains no recognized category or city token'
    };
  }

  return {
    valid: true,
    matched_category,
    matched_city
  };
}

/**
 * Step 3: Score opportunity for surviving keywords
 *
 * Formula:
 * opportunity_score =
 *     log10(volume) * 30
 *   + (100 - kd) * 0.4
 *   - (justdial_position <= 3 ? 25 : 0)   -- incumbent locked in
 *   + (no_strong_local_result ? 20 : 0)   -- weak SERP = opening
 *   + (registry_data_helps ? 15 : 0)      -- FSSAI/RERA/GST adds real value
 *   - (local_pack_present ? 15 : 0)
 */
export function calculateOpportunityScore(params: {
  volume: number;
  kd: number;
  competitorPos?: number;
  noStrongLocalResult?: boolean;
  registryDataHelps?: boolean;
  localPackPresent?: boolean;
}): number {
  const vol = Math.max(params.volume || 10, 10);
  const logVol = Math.log10(vol);
  const kd = Math.min(Math.max(params.kd || 0, 0), 100);

  let score = logVol * 30 + (100 - kd) * 0.4;

  const pos = params.competitorPos ?? 10;
  if (pos <= 3) {
    score -= 25; // Incumbent locked in
  } else if (pos >= 5 && pos <= 20) {
    score += 15; // Contested ranking = opening
  }

  if (params.noStrongLocalResult) score += 20;
  if (params.registryDataHelps) score += 15;
  if (params.localPackPresent) score -= 15;

  return Number(Math.max(score, 0).toFixed(2));
}

export interface SemrushCsvRow {
  Keyword: string;
  Volume?: string | number;
  'Keyword Difficulty'?: string | number;
  KD?: string | number;
  Position?: string | number;
}

export interface ProcessedKeywordRecord {
  keyword: string;
  volume: number;
  kd: number;
  intent_tier: 'TIER_1' | 'TIER_2' | 'TIER_3' | 'REJECTED';
  reject_reason?: string;
  category_name?: string;
  city_name?: string;
  opportunity_score: number;
}

/**
 * Step 4: Bulk Process Semrush Export Rows
 */
export function processSemrushKeywords(
  rows: SemrushCsvRow[],
  knownCategories: string[],
  knownCities: string[]
): ProcessedKeywordRecord[] {
  return rows.map((row) => {
    const kw = (row.Keyword || '').trim();
    const volume = Number(row.Volume || 0);
    const kd = Number(row['Keyword Difficulty'] || row.KD || 0);
    const pos = Number(row.Position || 10);

    const intent = classifyKeywordIntent(kw);
    if (intent.verdict === 'REJECT') {
      return {
        keyword: kw,
        volume,
        kd,
        intent_tier: 'REJECTED',
        reject_reason: intent.reason,
        opportunity_score: 0
      };
    }

    const entity = entityCheck(kw, knownCategories, knownCities);
    if (!entity.valid) {
      return {
        keyword: kw,
        volume,
        kd,
        intent_tier: 'REJECTED',
        reject_reason: entity.reason,
        opportunity_score: 0
      };
    }

    const opportunity_score = calculateOpportunityScore({
      volume,
      kd,
      competitorPos: pos
    });

    return {
      keyword: kw,
      volume,
      kd,
      intent_tier: intent.verdict,
      category_name: entity.matched_category,
      city_name: entity.matched_city,
      opportunity_score
    };
  });
}
